<template>
  <div class="report-page">
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Customer Report</h5>
        <p class="page-sub">View sales performance broken down by customer</p>
      </div>

      <div class="page-actions">
        <!-- Search -->
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-icon" />
          <input
            type="text"
            class="search-input"
            placeholder="Search customer name..."
            v-model="query"
          />
        </div>

        <!-- Export -->
        <div class="dropdown" ref="exportDrop">
          <button class="btn-outline" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu" :class="{ show: exportOpen }">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportPdf">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="reusableMixinToCSV('Customer-sales'); exportOpen = false">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid" v-if="details.length > 0 && !loading">
      <div class="summary-card">
        <div class="summary-icon summary-icon--blue">
          <font-awesome-icon icon="users" />
        </div>
        <div>
          <div class="summary-label">Customers</div>
          <div class="summary-value">{{ pagination.total }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--green">
          <font-awesome-icon icon="receipt" />
        </div>
        <div>
          <div class="summary-label">Total Transactions</div>
          <div class="summary-value">{{ total_transaction }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--amber">
          <font-awesome-icon icon="coins" />
        </div>
        <div>
          <div class="summary-label">Total Amount</div>
          <div class="summary-value">
            {{ company?.currency?.symbol }}{{ $filters.formatMoney(total_amount) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading customer data...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="users" />
        </div>
        <p class="state-title">No customers found</p>
        <p class="state-sub">
          {{ query ? 'No results match your search. Try a different name.' : 'No customer sales data available yet.' }}
        </p>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong> customers
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option :value="pagination.total">All</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer Name</th>
                <th class="text-right">Transactions</th>
                <th class="text-right">Total Amount ({{ company?.currency?.symbol }})</th>
                <th class="col-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(customer, index) in details" :key="customer.id">
                <td class="text-muted-cell">{{ pagination.from + index }}</td>

                <td>
                  <div class="customer-cell">
                    <div class="customer-avatar">{{ initials(customer.customer_name) }}</div>
                    <span class="customer-name">{{ customer.customer_name }}</span>
                  </div>
                </td>

                <td class="text-right">
                  <span class="txn-badge">{{ customer.transactions }}</span>
                </td>

                <td class="text-right fw-bold">
                  {{ $filters.formatMoney(customer.total_amt) }}
                </td>

                <td class="col-actions">
                  <router-link
                    class="view-btn"
                    :to="$url(`/user-customer-report/${customer.id}?store=${tableData.store}`)"
                  >
                    <font-awesome-icon icon="eye" />
                    View
                  </router-link>
                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr class="grand-total-row">
                <td colspan="2" class="grand-label">Grand Total</td>
                <td class="text-right">
                  <span class="txn-badge txn-badge--total">{{ total_transaction }}</span>
                </td>
                <td class="text-right fw-bold">
                  {{ $filters.formatMoney(total_amount) }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Pagination -->
        <div class="table-footer">
          <pagination
            :pagination="pagination"
            @prev="tableData.page = pagination.currentPage - 1; fetchData()"
            @next="tableData.page = pagination.currentPage + 1; fetchData()"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import ReportHeaderTab from './components/reportheadertab.vue';

export default {
  name: 'CustomerReport',

  components: { ReportHeaderTab },

  mixins: [reusableMixin],

  data() {
    return {
      print_content:     [],
      headerArrayColumn: ['S/N', 'Customer Name', 'Transactions', 'Total Amount'],
      footerRowArray:    [],

      details:     [],
      loading:     false,
      crudLoading: false,
      exportOpen:  false,
      query:       null,

      tableData: { page: 1,
        store:        'All',
        start_date:   null,
        end_date:     null,
        draw:         0,
        length:       10,
        search:       '',
        column:       0,
        dir:          'desc',
        status:       [0, 1, 2, 3],
        query:        '',
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      total_transaction: 0,
      total_amount:      0,

      toast: useToast(),
    };
  },

  methods: {
    initials(name) {
      if (!name) return '?';
      return name.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase();
    },

    exportPdf() {
      this.exportOpen = false;
      this.drawPdf(
        this.print_content,
        this.headerArrayColumn,
        'l',
        'customer-sales',
        'Customer Sales',
        '',
        this.footerRowArray,
      );
    },

    grandTotal(items) {
      this.total_transaction = items.reduce((s, t) => s + t.transactions, 0);
      this.total_amount      = items.reduce((s, t) => parseFloat((parseFloat(s) + parseFloat(t.total_amt)).toFixed(2)), 0);
    },

    dataToPrint(details) {
      this.print_content = details.map((s, idx) => ({
        sn:           idx + 1,
        customer_name: s.customer_name,
        transactions:  s.transactions,
        total_amount:  this.$filters.formatMoney(s.total_amt),
      }));
      this.footerRowArray = [
        '', 'GRAND TOTAL', this.total_transaction,
        this.$filters.formatMoney(this.total_amount),
      ];
    },

    fetchData(url = '/report/customer-report') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;
      this.tableData.draw++;

      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          this.details = data.data.customers.data;
          this.grandTotal(data.data.customers2);
          this.dataToPrint(this.details);
          this.configPagination(data.data.customers);
        })
        .catch(() => {
          this.toast.error('Failed to load customer data. Please try again.');
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
          this.print_content = [];
        });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  watch: {
    query() { this.fetchData(); },
  },

  created() {
    this.fetchData();
    this.allStore();
    this.getCompany();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.report-page { display:flex; flex-direction:column; gap:18px; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }

/* ── Search ──────────────────────────────────────────────────────────── */
.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:220px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }

/* ── Dropdown ────────────────────────────────────────────────────────── */
.dropdown      { position:relative; }
.dropdown-menu { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; }
.dropdown-item:hover { background:#f4f7fb; }
.me-2 { margin-right:8px; }

/* ── Summary cards ───────────────────────────────────────────────────── */
.summary-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; }
.summary-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.summary-icon { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.summary-icon--blue  { background:rgba(0,74,173,.1); color:#004AAD; }
.summary-icon--green { background:#f0fdf4; color:#16a34a; }
.summary-icon--amber { background:#fff7ed; color:#e08c00; }
.summary-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:5px; }
.summary-value { font-size:20px; font-weight:700; color:#1a2b45; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right  { text-align:right; }
.fw-bold     { font-weight:700; }
.col-actions { width:80px; text-align:center; }

/* ── Customer cell ───────────────────────────────────────────────────── */
.customer-cell   { display:flex; align-items:center; gap:10px; }
.customer-avatar { width:32px; height:32px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.customer-name   { font-size:13px; font-weight:500; color:#1a2b45; }

/* ── Transaction badge ───────────────────────────────────────────────── */
.txn-badge { display:inline-flex; align-items:center; justify-content:center; background:#f4f7fb; color:#1a2b45; border:1px solid #e4eaf2; border-radius:6px; padding:2px 10px; font-size:12px; font-weight:600; }
.txn-badge--total { background:rgba(0,74,173,.08); color:#004AAD; border-color:rgba(0,74,173,.18); }

/* ── View button ─────────────────────────────────────────────────────── */
.view-btn { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#004AAD; border:1px solid #c5d6f5; border-radius:7px; padding:5px 12px; font-size:12px; font-weight:600; text-decoration:none; transition:all .15s; }
.view-btn:hover { background:#e8f0fb; color:#003b8e; text-decoration:none; }

/* ── Grand total ─────────────────────────────────────────────────────── */
.grand-total-row { background:#f8fafc; border-top:2px solid #e4eaf2; }
.grand-total-row td { padding:13px 16px; font-size:13px; }
.grand-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header, .page-actions { flex-direction:column; align-items:stretch; }
  .search-input { width:100%; }
  .summary-grid { grid-template-columns:1fr; }
}
@media (min-width:769px) and (max-width:1024px) {
  .summary-grid { grid-template-columns:repeat(3,1fr); }
}
</style>